import React from 'react';
import styled from '@emotion/styled';
import { colors, fontSize } from 'global/variables';
import { getUtcDateFormatted } from 'utils/timeFormatHelpers';
import withTranslation from 'hoc/withTranslation';
import { BreakIcon, SquareRechargeIcon, SquareParkingIcon } from 'icons/EVIcons';
import { isBreak, isBreakOrReload, isRecharge, isDepartureOrArrival } from 'utils/SolutionHelpers';
import { getOrdersByInternalID } from 'utils/OrdersHelpers';
import moment from 'moment';

const { secondaryTextColor, secondaryMarkerColor, white } = colors;
const { small } = fontSize;

const StyledStopDetails = styled.div({
  lineHeight: '1rem',
  padding: '0.2rem',
  color: secondaryTextColor,
  fontSize: small,
  backgroundColor: white,
  paddingBottom: '0.5rem',
  animation: 'EntryAnimation .3s 1',
  animationTimingFunction: 'ease-out',
  fontWeight: '400',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
});

const StyledJobId = styled.span({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
});

const StyledStopDetailsSpanHeader = styled.div({
  paddingTop: '0.6rem',
  paddingBottom: '0',
  textTransform: 'uppercase',
  textAlign: 'left',
  width: '100%',
  color: secondaryMarkerColor,
  animation: 'EntryAnimationHeader .3s 1 !important',
  animationTimingFunction: 'ease-out',
  fontWeight: '400',
  fontSize: small,
});

const StyledIconWrapper = styled.span({
  display: 'inline-flex',
  width: '18px',
  height: '18px',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',
  '& svg': {
    width: '18px',
    height: '18px',
  },
});

const formatWalkDistance = (meters) => {
  if (meters === null || meters === undefined) return '–';
  if (meters >= 1000) return `${(meters / 1000).toFixed(1)}km`;
  return `${Math.round(meters)}m`;
};

const ActivityTypeIcon = ({ type, labels }) => {
  const color = secondaryTextColor;

  if (type === 'delivery') {
    return (
      <StyledIconWrapper title={labels.deliveryLabel}>
        <svg viewBox="0 0 16 16" fill="none">
          <path d="M8 2L8 11" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
          <path
            d="M4 8L8 13L12 8"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledIconWrapper>
    );
  }
  if (type === 'pickup') {
    return (
      <StyledIconWrapper title={labels.pickupLabel}>
        <svg viewBox="0 0 16 16" fill="none">
          <path d="M8 14L8 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
          <path
            d="M4 8L8 3L12 8"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledIconWrapper>
    );
  }
  if (type === 'break') {
    return (
      <StyledIconWrapper title={labels.breakLabel}>
        <BreakIcon color={color} />
      </StyledIconWrapper>
    );
  }
  if (
    type === 'drivingRestTime' ||
    type === 'workingRestTime' ||
    type === 'dutyWorkingRestTime' ||
    type === 'dutyDrivingRestTime'
  ) {
    return (
      <StyledIconWrapper title={labels[`${type}Label`]}>
        <BreakIcon color={color} />
      </StyledIconWrapper>
    );
  }
  if (type === 'reload') {
    return (
      <StyledIconWrapper title={labels.reloadLabel}>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M20 8A9 9 0 1 0 20.5 13"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M20 4V8.5H15.5"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </StyledIconWrapper>
    );
  }
  if (type === 'recharge') {
    return (
      <StyledIconWrapper title={labels.rechargeLabel}>
        <SquareRechargeIcon />
      </StyledIconWrapper>
    );
  }
  if (type === 'parking' || type === 'backToVehicle') {
    return (
      <StyledIconWrapper title={labels?.parkingLabel}>
        <SquareParkingIcon />
      </StyledIconWrapper>
    );
  }
  if (type === 'pudo') {
    return (
      <StyledIconWrapper title={labels.pudoLabel}>
        <svg viewBox="3 3 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 4L19 8L19 16L12 20L5 16L5 8Z"
            stroke={color}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M19 8L12 12L5 8"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M12 12L12 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </StyledIconWrapper>
    );
  }
  return (
    <span style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.02em', color }}>
      {type ? type.slice(0, 3) : '–'}
    </span>
  );
};

const StopDetails = ({
  stop,
  offset,
  walkingDistancesMap,
  hasWalkData,
  orders,
  translations: {
    global: activityTypeLabels,
    tourSolution: {
      activityHeaders: { id, arrival, type, walk, consumption },
    },
  },
}) => {
  const { activities } = stop;
  const hasConsumptionData = stop.consumption != null;

  const isDepartureOrArrivalStop = activities.some((a) => isDepartureOrArrival(a.type));
  const hasActivitiesWithLocation = activities.some(
    (a) =>
      a.location &&
      a.location.lat === stop.location.lat &&
      a.location.lng === stop.location.lng &&
      !isBreak(a.type) &&
      !isDepartureOrArrival(a.type) &&
      a.type !== 'backToVehicle',
  );
  const hasActivitiesAtDifferentLocation = activities.some(
    (a) =>
      a.location &&
      (a.location.lat !== stop.location.lat || a.location.lng !== stop.location.lng) &&
      a.type !== 'backToVehicle',
  );
  const isBreakReloadOrRecharge = activities.some((a) => isBreakOrReload(a.type) || isRecharge(a.type));
  const isParking =
    !isDepartureOrArrivalStop && !hasActivitiesWithLocation && hasActivitiesAtDifferentLocation && !isBreakReloadOrRecharge && activities.length > 0;

  let parkingDuration = null;
  let parkingId = null;
  if (isParking) {
    const firstActivity = activities.find((a) => !isBreak(a.type) && !isDepartureOrArrival(a.type));
    if (firstActivity?.time?.start && stop.time?.arrival) {
      const stopArrival = moment(stop.time.arrival);
      const firstActivityStart = moment(firstActivity.time.start);
      if (stopArrival.isValid() && firstActivityStart.isValid()) {
        parkingDuration = firstActivityStart.diff(stopArrival, 'minutes');
      }
    }
    const relatedOrder = getOrdersByInternalID(orders, firstActivity?.jobId)?.[0];
    const backToVehicleActivity = activities.find((a) => a.type === 'backToVehicle');
    parkingId = relatedOrder?.ParkingId || backToVehicleActivity?.jobId || null;
  }

  return (
    <>
      <StyledStopDetails>
        <StyledStopDetailsSpanHeader>{id}</StyledStopDetailsSpanHeader>
      </StyledStopDetails>
      <StyledStopDetails>
        <StyledStopDetailsSpanHeader>{arrival}</StyledStopDetailsSpanHeader>
      </StyledStopDetails>
      <StyledStopDetails style={{ paddingLeft: '2rem' }}>
        <StyledStopDetailsSpanHeader>{type}</StyledStopDetailsSpanHeader>
      </StyledStopDetails>
      <StyledStopDetails>
        {hasWalkData && <StyledStopDetailsSpanHeader>{walk}</StyledStopDetailsSpanHeader>}
        {!hasWalkData && hasConsumptionData && (
          <StyledStopDetailsSpanHeader>{consumption}</StyledStopDetailsSpanHeader>
        )}
      </StyledStopDetails>
      <StyledStopDetails />
      {isParking && (
        <>
          <StyledStopDetails data-test-id="StopDetailParkingId">
            <StyledJobId title={parkingId || '–'}>{parkingId || '–'}</StyledJobId>
          </StyledStopDetails>
          <StyledStopDetails data-test-id="StopDetailParkingArrival">
            {getUtcDateFormatted(stop.time.arrival, offset)}
          </StyledStopDetails>
          <StyledStopDetails data-test-id="StopDetailParkingType" style={{ paddingLeft: '2rem' }}>
            <ActivityTypeIcon type="parking" labels={activityTypeLabels} />
          </StyledStopDetails>
          <StyledStopDetails data-test-id="StopDetailParkingDuration">
            {parkingDuration != null && parkingDuration > 0 ? `${parkingDuration}m` : '–'}
          </StyledStopDetails>
          <StyledStopDetails />
        </>
      )}
      {activities &&
        activities.filter((a) => !(isParking && a.type === 'backToVehicle')).map((activity, i) => {
          const loc = activity.location || stop.location;
          const key = `${parseFloat(loc.lat).toFixed(5)},${parseFloat(loc.lng).toFixed(5)}`;
          const walkDistance = walkingDistancesMap?.[key];
          return (
            <React.Fragment key={i}>
              <StyledStopDetails data-test-id="StopDetailId">
                <StyledJobId title={activity.jobId || '–'}>{activity.jobId || '–'}</StyledJobId>
              </StyledStopDetails>
              <StyledStopDetails data-test-id="StopDetailArrival">
                {getUtcDateFormatted(
                  activity.time ? activity.time.start : stop.time.arrival,
                  offset,
                )}
              </StyledStopDetails>
              <StyledStopDetails data-test-id="StopDetailType" style={{ paddingLeft: '2rem' }}>
                <ActivityTypeIcon type={activity.type} labels={activityTypeLabels} />
              </StyledStopDetails>
              <StyledStopDetails
                data-test-id={hasWalkData ? 'StopDetailWalk' : 'StopDetailConsumption'}
              >
                {hasWalkData && formatWalkDistance(walkDistance)}
                {!hasWalkData && hasConsumptionData && `${stop.consumption.toFixed(2)} kWh`}
              </StyledStopDetails>
              <StyledStopDetails />
            </React.Fragment>
          );
        })}
    </>
  );
};

export default withTranslation(StopDetails);
